import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import {
  Box,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import {
  HtmlTooltip,
  IconContainer,
  IconText,
  StyledMainBox,
  StyledRightAlignedStack,
} from "../../../components/commonComponents/StyledComponents/styled";

import { Add, Close, Delete, DocumentScannerOutlined, Download, Store } from "@mui/icons-material";
import ActionButton, { TextButton } from "../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import * as xlsx from "xlsx";
import AddNewMarketingActivity from "../ModalContent/AddMarketingActivity";
import CustomBadge from "../../../components/commonComponents/Badge/CustomBadge";
import DeleteActivity from "../ModalContent/DeleteActivityConfirmation";
import ActivityInfo from "../ModalContent/ActivityInfo";
import StoreInfo from "../ModalContent/StoreInfo";
import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import {
  ListingCellLeft,
  ListingCellCenter,
  ListingCellRight,
  ListingIconMetric,
  ListingMetricValue,
} from "../../../components/commonComponents/TabularLists/styles";
import { resetMarketingActivitiesData } from "../../../api_calls/marketingActivities";

const MarketingActivitiesList = ({ open, onClose, size, usedFor, closeModal, campaignRecord }) => {
  const sheetDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [newActivity, setNewActivity] = React.useState(false);
  const [deleteActivity, setDeleteActivityModal] = React.useState(false);
  const [gtinInfo, setGtinInfo] = React.useState([]);
  const [showInfo, setShowInfo] = React.useState(false);
  const [activeStoreActivity, setActiveStoreActivity] = React.useState(null);

  const id = campaignRecord.campaign_id;

  const dataForDeletion = (activityRecord) => {
    if (typeof activityRecord !== "undefined" && Object.keys(activityRecord).length !== 0) {
      dispatchAction(
        updateStateData({
          activity_ids_to_delete: activityRecord.id_marketing_activity.split(",").map((item) => parseInt(item)),
        })
      );
    }
  };

  const exportToExcel = () => {
    // Convert JSON data to an array of arrays
    const data = [];

    data.push([
      "Activity",
      "Week",
      "GTIN",
      "Description",
      "Stores"
    ]); // Add headers

    sheetDetails.activities_data
      .slice()
      .sort((a, b) => {
        return a.week - b.week;
      }).forEach((item) => {
        let gtinDescriptions = {}
        // find gtin descriptions for respective gtin numbers
        item.gtin.forEach((gtinNum) => {
          const gtinVals = sheetDetails.gtin_list.filter((gtinItem) => {
            return Number(gtinItem.value) === Number(gtinNum);
          });
          gtinDescriptions = {
            ...gtinDescriptions,
            [gtinNum]: gtinVals.length !== 0 ? gtinVals[0].description : gtinNum,
          }
        });
        // add rows for each store name and gtin
        item.store_names.forEach((storeName) => {
          item.gtin.forEach((gtin) => {
            const row = [];
            row.push(
              item.name_activity,
              item.week[0],
              gtin,
              gtinDescriptions[gtin],
              storeName,
              // item.discounted_price,
              // item.original_price
            );
            data.push(row);
          });

        });
      });

    // Create a new workbook and add a worksheet
    const workbook = xlsx.utils.book_new();
    const worksheet = xlsx.utils.aoa_to_sheet(data);

    // Add the worksheet to the workbook
    xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    let exportFileName =
      typeof sheetDetails.campaign_detail !== "undefined"
        ? "export_" + sheetDetails.campaign_detail.campaign_name + "_marketing_activities.xlsx"
        : "marketingActivities.xlsx";

    // Save the workbook to an Excel file
    xlsx.writeFile(workbook, exportFileName);
  };

  const rows = React.useMemo(() => {
    const list = sheetDetails.activities_data ?? [];
    return [...list].sort((a, b) => {
      const aWeek = Array.isArray(a.week) ? a.week[0] : a.week;
      const bWeek = Array.isArray(b.week) ? b.week[0] : b.week;
      return (aWeek ?? 0) - (bWeek ?? 0);
    });
  }, [sheetDetails.activities_data]);


  const columns = React.useMemo(
    () => [
      {
        id: "activity",
        header: "Activity",
        cellClassName: "text-wrapper wrapLargeText",
        render: (activity) => (
          <ListingCellLeft>
            {activity.name_activity}
          </ListingCellLeft>
        ),
      },

      {
        id: "week",
        header: "Week",
        render: (activity) => (
          <ListingCellLeft>
            {(activity.week ?? []).map((weekNum) => (
              <CustomBadge key={weekNum + "_week"} label={weekNum} />
            ))}
          </ListingCellLeft>
        ),
      },

      {
        id: "gtin",
        header: "GTIN",
        headerAlign: "center",
        align: "center",
        render: (activity) => (
          <ListingCellCenter>
            <Link
              href="#"
              underline="none"
              sx={{ color: "#AF3241" }}
              onClick={(e) => {
                e.preventDefault();
                setGtinInfo(activity);
                setShowInfo(true);
              }}
            >
              <HtmlTooltip
                title={
                  <Box width={300} sx={{ wordBreak: "break-word" }}>
                    <Typography variant="body2" color="inherit" fontWeight={600}>
                      Click to see all GTINs:
                    </Typography>
                    <ul style={{ columns: 2 }}>
                      {(activity.gtin ?? []).slice(0, 30).map((gtin, index) => (
                        <li key={index + "_gtin"} style={{ margin: "5px" }}>
                          {gtin}
                        </li>
                      ))}
                      {(activity.gtin ?? []).length > 30 && (
                        <li style={{ listStyleType: "none", margin: "5px" }}>
                          <Typography variant="body2" color="inherit" fontWeight={600}>
                            + {(activity.gtin ?? []).length - 30} more GTINs
                          </Typography>
                        </li>
                      )}
                    </ul>
                  </Box>
                }
              >
                <ListingIconMetric>
                  <DocumentScannerOutlined sx={{ color: "#AF3241", transform: "rotate(90deg)" }} />
                  <ListingMetricValue>
                    <IconText variant="body2">{(activity.gtin ?? []).length}</IconText>
                  </ListingMetricValue>
                </ListingIconMetric>
              </HtmlTooltip>
            </Link>
          </ListingCellCenter>
        ),
      },

      {
        id: "stores",
        header: "Stores",
        headerAlign: "center",
        align: "center",
        render: (activity) => (
          <ListingCellCenter>
            <Link
              href="#"
              underline="none"
              sx={{ color: "#AF3241" }}
              onClick={(e) => {
                e.preventDefault();
                setActiveStoreActivity(activity);
              }}
            >
              <ListingIconMetric>
                <Store sx={{ color: "#AF3241" }} />
                <ListingMetricValue>
                  <IconText variant="body2">{(activity.store_names ?? []).length}</IconText>
                </ListingMetricValue>
              </ListingIconMetric>
            </Link>
          </ListingCellCenter>
        ),
      },

      {
        id: "action",
        header: "Action",
        headerAlign: "center",
        align: "center",
        render: (activity) => (
          <ListingCellCenter>
            {![2, 4].includes(campaignRecord.campaign_status_code) && (
              <HtmlTooltip title="Delete">
                <a
                  href="#deleteActivity"
                  aria-label="delete activity"
                  onClick={(e) => {
                    e.preventDefault();
                    dataForDeletion(activity);
                    setDeleteActivityModal(true);
                  }}
                >
                  <Delete sx={{ color: "#AF3241" }} />
                </a>
              </HtmlTooltip>
            )}
          </ListingCellCenter>
        ),
      },
    ],
    [campaignRecord.campaign_status_code]
  );


  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth={size}
      >
        <DialogTitle>{"Marketing Activities"}</DialogTitle>
        <DialogContent>
          <Box height={20} />
          {![2, 4].includes(campaignRecord.campaign_status_code) && (
            <>
              <Box>
                <Stack direction={"row"} spacing={4}>
                  <ActionButton
                    onClick={(e) => {
                      e.preventDefault();
                      setNewActivity(true);
                    }}
                    label={"Add New Activity"}
                    icon={<Add />}
                  />
                </Stack>
              </Box>
              <Box height={20} />
            </>
          )}

          {/* --------- show campaign sheet content along with checkboxes --------- */}
          <Stack direction={"row"} spacing={4}>
            {typeof sheetDetails.activities_data !== "undefined" && sheetDetails.activities_data.length !== 0 ? (
              <ListingDataTableForModal
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id_marketing_activity ?? row.name_activity}
                emptyText="No marketing activities added"
                footer={null}
                tableLayout={"fixed"}
              />
            ) : (
              <div className="row mb-3">
                <div className="col-md-12">No marketing activities added</div>
              </div>
            )}
          </Stack>
          <Box height={40} />
          <StyledRightAlignedStack direction={"row"} spacing={1} sx={{ marginRight: "0px" }}>
            {/* ------ export to excel button ------- */}
            {typeof sheetDetails.activities_data !== "undefined" && sheetDetails.activities_data.length !== 0 && (
              <TextButton
                onClick={(e) => {
                  e.preventDefault();
                  exportToExcel();
                }}
                label={"Download Excel"}
                icon={<Download />}
              />
            )}
          </StyledRightAlignedStack>
        </DialogContent>
        <Box height={20} />
        <DialogActions>
          <Stack direction={"row"} spacing={2}>
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                closeModal();
              }}
              label="Cancel"
              icon={<Close />}
            />
          </Stack>
        </DialogActions>
      </Dialog >

      {/* ---------- new marketing activity popup ---------- */}
      {
        newActivity === true && (
          <AddNewMarketingActivity
            open={newActivity}
            onClose={(e) => {
              e.preventDefault();
            }}
            size={"md"}
            closeModal={() => {
              setNewActivity(false);
              resetMarketingActivitiesData();
            }}
            campaignRecord={campaignRecord}
            usedFor={"add"}
          />
        )
      }
      {/* ------- gtin info ------ */}
      {
        showInfo === true && (
          <ActivityInfo
            open={showInfo}
            onClose={() => {
              setShowInfo(false);
            }}
            size={"md"}
            gtinInfo={gtinInfo}
            usedFor={"gtins"}
            closeModal={() => {
              setShowInfo(false);
            }}
          />
        )
      }
      {/* ------- store names info ------ */}
      {
        activeStoreActivity !== null && (
          <StoreInfo
            open={true}
            onClose={() => {
              setActiveStoreActivity(null);
            }}
            size={"md"}
            storeInfo={{
              selected_store_options: activeStoreActivity.store_names.map(storeName => {
                return { label: storeName, value: storeName }
              }),
              name: activeStoreActivity.name_activity,
              stores: activeStoreActivity.store_names.length,
            }}
            usedFor={"Activity"}
            dialogTitle={"Marketing Activity Stores"}
            closeModal={() => {
              setActiveStoreActivity(null);
            }}
          />
        )
      }

      {/* --------------- Delete activity modal ----------- */}
      {
        deleteActivity === true && (
          <DeleteActivity showdeleteActivityPopup={deleteActivity} closePopup={setDeleteActivityModal} />
        )
      }
    </>
  );
};

export default MarketingActivitiesList;
